import { Form, Formik, FormikHelpers } from "formik";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import { DetailField } from "@/core/components/detail-field";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Textarea } from "@/core/components/ui/textarea";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { BusinessSettingsSidebarHeader } from "@/business-settings/common/business-settings-sidebar-header";
import { BusinessSettingsSidebarWrapper } from "@/business-settings/common/business-settings-sidebar-wrapper";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { formatDate } from "@/core/utils/date";
import { formatMoneyCents } from "@/core/utils/money";
import { formatPhoneNumber } from "@/core/utils/phone";
import { useGetPayout, useUpdatePayout } from "@/payouts/query";
import { ReferralPaymentsSidebarSkeleton } from "./referral-payments-sidebar-skeleton";

interface ReferralPaymentFormValues {
  notes: string;
}

interface ReferralPaymentsSidebarProps {
  referralPaymentsId: string;
  businessId?: string;
}

const validationSchema = Yup.object().shape({
  notes: Yup.string().optional(),
});

const ReferralPaymentsSidebar = ({
  referralPaymentsId,
  businessId,
}: ReferralPaymentsSidebarProps) => {
  const { data: payout, isLoading } = useGetPayout(
    businessId,
    referralPaymentsId,
  );

  const { mutate: updatePayout } = useUpdatePayout(businessId);
  const { resetSidebar } = useBusinessSettingsSidebar();
  const [isEditingNote, setIsEditingNote] = useState(false);

  if (isLoading) {
    return (
      <BusinessSettingsSidebarWrapper>
        <LoadingSpinner />
      </BusinessSettingsSidebarWrapper>
    );
  }

  if (!payout) {
    return (
      <BusinessSettingsSidebarWrapper>
        <div className="p-5">Payment not found.</div>
      </BusinessSettingsSidebarWrapper>
    );
  }

  const potentialCustomerNameOrPhone = useMemo(
    () =>
      payout.referral?.potential_customer_name ||
      (payout.referral?.potential_customer_phone_number
        ? formatPhoneNumber(payout.referral.potential_customer_phone_number)
        : "N/A"),
    [payout.referral],
  );

  const advocateNameOrPhone = useMemo(
    () =>
      payout.referral?.advocate_name ||
      (payout.referral?.advocate_phone_number
        ? formatPhoneNumber(payout.referral.advocate_phone_number)
        : "N/A"),
    [payout.referral],
  );

  const initialValues = useMemo<ReferralPaymentFormValues>(
    () => ({
      notes: payout.notes || "",
    }),
    [payout],
  );

  const handleSubmit = async (
    values: ReferralPaymentFormValues,
    { resetForm }: FormikHelpers<ReferralPaymentFormValues>,
  ) =>
    updatePayout(
      {
        ...values,
        id: payout.id,
      },
      {
        onSuccess: () => {
          resetForm({ values });
          toast.success("Referral payment updated successfully");
          setIsEditingNote(false);
        },
        onError: (error) => {
          toast.error("Failed to update referral payment", {
            description:
              error instanceof Error ? error.message : "Please try again",
          });
          setIsEditingNote(false);
        },
      },
    );

  const headerText = "Payments";
  const name = "Payment made";

  if (isLoading) {
    return <ReferralPaymentsSidebarSkeleton />;
  }

  return (
    <BusinessSettingsSidebarWrapper>
      <BusinessSettingsSidebarHeader
        headerText={headerText}
        name={name}
        onClose={resetSidebar}
      />
      <div className="flex-1 overflow-hidden">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ getFieldProps, dirty, resetForm }) => (
            <Stack
              gap="lg"
              className="flex h-full flex-col overflow-hidden"
              fullWidth
            >
              <Form className="w-full flex-none px-6 pt-6">
                <Stack gap="lg" fullWidth>
                  <Stack direction="row" fullWidth>
                    <DetailField
                      label="Customer"
                      message={potentialCustomerNameOrPhone}
                      extraInfo={
                        <>
                          This customer booked a job with you through{" "}
                          <b>{advocateNameOrPhone}</b>
                          <br />
                          We use their name or phone number to help you identify
                          them and all of the things that happen related to the
                          job you did for them.
                        </>
                      }
                    />
                    <DetailField
                      label="Advocate"
                      message={advocateNameOrPhone}
                      extraInfo={
                        <>
                          This is the customer that referred{" "}
                          <b>{potentialCustomerNameOrPhone}</b> to you
                        </>
                      }
                    />
                  </Stack>

                  <Stack direction="row" fullWidth>
                    <DetailField
                      label="Date paid"
                      message={formatDate(payout.created_at)}
                    />
                    <DetailField
                      label="Initiated by"
                      message={`${payout.approver.first_name ?? ""} ${payout.approver.last_name ?? ""}`}
                      extraInfo={
                        <>
                          This is the user who initiated the payment to{" "}
                          <b>{advocateNameOrPhone}</b>
                        </>
                      }
                    />
                  </Stack>

                  <DetailField
                    label="Payment amount"
                    message={formatMoneyCents(payout.amount)}
                  />

                  <Stack className="relative" fullWidth>
                    {payout.notes && !isEditingNote ? (
                      <DetailField label="Note" message={payout.notes} />
                    ) : (
                      <Textarea
                        label="Note"
                        placeholder="Enter notes"
                        {...getFieldProps("notes")}
                      />
                    )}

                    {payout.notes && !isEditingNote && (
                      <Typography
                        variant="subtitleW3"
                        textColor="interactive"
                        className="absolute right-0 top-[3px] cursor-pointer"
                        onClick={() => {
                          setIsEditingNote(true);
                        }}
                      >
                        Edit
                      </Typography>
                    )}
                  </Stack>

                  {(dirty || isEditingNote) && (
                    <Stack direction="row">
                      <Button type="submit" size="sm" disabled={!dirty}>
                        Save
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          resetForm({ values: initialValues });
                          setIsEditingNote(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Form>
            </Stack>
          )}
        </Formik>
      </div>
    </BusinessSettingsSidebarWrapper>
  );
};

export default ReferralPaymentsSidebar;
